import { wrapTextWithSpan } from '../../../ts/helper';
import { lazyLoading } from '../../../ts/lazyLoading';
import { smoothScrollToElement } from '../../../ts/smoothScrolling';

/** Timeout for each fetch request in milliseconds. When the timeout exceeds, we abort the fetch. Then the target search result does not have a describing text – which is okay. */
const fetchTimeout =
  document.documentElement.getAttribute('data-env') === 'production'
    ? 3000
    : 10000;

/** Global array to track active fetch requests */
let activeRequests: AbortController[] = [];

/**
 * Cleanup function to abort all pending requests
 */
const cleanupRequests = () => {
  activeRequests.forEach((controller) => {
    controller?.abort();
  });
  activeRequests = [];
};

/**
 * Populates the provided {@link textContainer} with text extracted from the given {@link html} string, highlighting the specified {@link keyword}. The function limits the number of paragraphs and characters per paragraph to be included in the text container. The function performs the following steps:
 * 1. Creates a temporary DOM element ({@link tempDom}) to parse the provided {@link html} string.
 * 2. Extracts all {@link paragraphs} within the main element of the temporary DOM.
 * 3. Filters paragraphs that contain the specified {@link keyword}.
 * 4. Limits the number of paragraphs ({@link paragraphLimit}) and characters per paragraph to be included ({@link characterLimitPerParagraph}).
 * 5. Replaces all '@' characters with '[at]' in the resulting text.
 * 6. Populates the text container with the resulting {@link text}.
 * 7. Wraps occurrences of the keyword in the text container with a span element for highlighting (with {@link wrapTextWithSpan}).
 *
 * @param html - The HTML string to parse and extract text from.
 * @param keyword - The keyword to search for and highlight within the text.
 * @param textContainer - The HTML element where the resulting text will be populated.
 *
 */
const popuplateText = (
  html: string,
  keyword: string,
  textContainer: HTMLElement
) => {
  /** Temporary DOM element to parse the {@link html} */
  const tempDom = document.createElement('div');
  tempDom.innerHTML = html;

  /** All relevant paragraphs in the {@link tempDom} */
  const paragraphs: HTMLParagraphElement[] = Array.from(
    tempDom.querySelectorAll('main p')
  );
  const paragraphsWithKeyword = paragraphs.filter((x) =>
    x.innerText.includes(keyword)
  );
  let text = '';

  const paragraphLimit = 3;
  const characterLimitPerParagraph = 50;

  let paragraphsToLoop = paragraphs;

  if (paragraphsWithKeyword.length) {
    paragraphsToLoop = paragraphsWithKeyword;
  }

  paragraphsToLoop.forEach((x, i) => {
    if (i <= paragraphLimit) {
      text += ` ${x.innerText.substring(0, characterLimitPerParagraph)}...`;
    }
  });

  text = text.replaceAll('@', '[at]');

  textContainer.innerText = text;
  textContainer.classList.add('loaded');

  wrapTextWithSpan(textContainer, keyword);
};

/**
 * Friendation component
 * Fetches search results for the specified keyword and populates the search results container with the results. The component also fetches text from the linked pages to build a preview text for each search result.
 * @param component Instance of this _Friendation_ component
 */
const basisSearchResults = async (component: HTMLElement) => {
  const messageElements = {
    /** X results message container */
    results: <HTMLElement>component.querySelector('.message--results'),

    /** No results message container */
    noResults: <HTMLElement>component.querySelector('.message--noResults'),

    /** Loading message container */
    loading: <HTMLElement>component.querySelector('.message--loading')
  };

  const formElements = {
    /** Search form */
    form: component.querySelector('form'),

    /** The search input that the user is typing in */
    searchInput: <HTMLInputElement>(
      component.querySelector('input[type="search"]')
    ),

    /** Submit button in {@link form} */
    submitButton: <HTMLButtonElement>component.querySelector('form button')
  };

  const resultsInnerText = messageElements.results.innerText;

  /** Container with results */
  const resultsList: HTMLUListElement =
    component.querySelector('.results-list');

  /**
   * Loop over all {@link results} and fetch text from the linked pages to build a preview text
   */
  const fetchPreviewText = () => {
    cleanupRequests();
    const keyword = formElements.searchInput.value.trim();

    const results = Array.from(
      component.querySelectorAll('.results-list > li')
    );

    /** Intersection observer to fetch text for each search result, once it becomes visible */
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(async (entry) => {
        if (entry.isIntersecting) {
          const result = entry.target as HTMLElement;
          const anchor = result.querySelector('a');

          /** The URL of the target page (that most likely contains the {@link keyword}) */
          const targetPageUrl = new URL(anchor.href).pathname;

          /** The element in {@link result} where the preview text will be populated */
          const textContainer: HTMLParagraphElement = result.querySelector(
            '.result-text-content'
          );

          /** Abort controller to abort the fetch request if it takes too long */
          const controller = new AbortController();

          /** Signal to pass to the fetch request. This signal will be aborted if the fetch takes too long */
          const signal = controller.signal;

          activeRequests.push(controller);

          try {
            /** Fetch the target page */
            const response: Response = await Promise.race<Response>([
              fetch(targetPageUrl, {
                signal
              }),
              new Promise<Response>((_, reject) =>
                setTimeout(() => reject(new Error('Timeout')), fetchTimeout)
              )
            ]);

            if (!response.ok) throw new Error('Failed to fetch');

            const text = await response.text();
            popuplateText(text, keyword, textContainer);
          } catch (error) {
            if (error.name !== 'AbortError') {
              console.warn(
                `Failed to fetch preview for ${targetPageUrl}:`,
                error
              );
            }
          } finally {
            // Remove this controller from active requests
            activeRequests = activeRequests.filter((c) => c !== controller);
          }

          // Unobserve the entry after fetching
          observer.unobserve(result);
        }
      });
    });

    results.forEach((result) => observer.observe(result));
  };

  const handleAjax = () => {
    formElements.form.addEventListener('submit', (e: SubmitEvent) => {
      cleanupRequests();
      e.preventDefault();

      /** The new term that the user wants to search */
      const term = formElements.searchInput.value.trim();

      /** Current url */
      const url = new URL(window.location.href);

      /** Current serach term */
      const currentTerm = url.searchParams.get('q')?.trim();

      /** Avoid submission of the same term */
      if (currentTerm && term === currentTerm) {
        smoothScrollToElement(resultsList);

        return;
      }

      resultsList.innerHTML = '';

      /** Avoid multiple form submits */
      if (formElements.form.hasAttribute('aria-busy')) return;
      formElements.form.setAttribute('aria-busy', 'true');
      if (formElements.submitButton) formElements.submitButton.disabled = true;

      /** Hide any messages */
      messageElements.results.inert = true;
      messageElements.noResults.inert = true;

      /** Show loading message */
      messageElements.loading.inert = false;

      /** Add new term to the url */
      url.searchParams.set('q', term);

      /** Modify current URL in browsers address bar */
      window.history.pushState(
        {
          url: url.toString()
        },
        '',
        url.toString()
      );

      /** Fetch results for the currently used search {@link term} */
      fetch(url.href, {})
        .then((x) => x.text())
        .then((x) => {
          const tempDom = document.createElement('div');
          tempDom.innerHTML = x;

          /** Results would be here */
          const items = Array.from(
            tempDom.querySelectorAll('.results-list > li')
          );

          /** Hide loading message */
          messageElements.loading.inert = true;

          if (items.length) {
            /** Update the „We found X results” message */
            updateSearchResultsCount(items.length);
            /** Show the „We found X results” message */
            messageElements.results.inert = false;
          } else {
            /** Show the „We found nothing” message */
            messageElements.noResults.inert = false;
          }

          /** Clear old items */
          resultsList.innerHTML = '';

          /** Append new items */
          items.forEach((item) => {
            /** Trigger animation */
            item.classList.add('animation--started');
            resultsList.append(item);
          });
        })
        .then(() => {
          setTimeout(() => {
            if (resultsList.childNodes.length) {
              // any results found
              resultsList.inert = false;
              smoothScrollToElement(resultsList);
              lazyLoading();
            } else {
              // no results found
              resultsList.inert = true;
              smoothScrollToElement(messageElements.noResults);
            }

            /** Make form accessible again */
            formElements.form.removeAttribute('aria-busy');
            if (formElements.submitButton)
              formElements.submitButton.disabled = false;

            fetchPreviewText();
          }, 100);
        });
    });
  };

  const updateSearchResultsCount = (amount?: number) => {
    if (!amount) amount = resultsList.childElementCount;
    messageElements.results.innerHTML = `<h2>${resultsInnerText
      .replace('%results%', amount.toString())
      .trim()}</h2>`;
  };

  /** Performance: cleanup all pending requests when the user navigates away */
  window.addEventListener('beforeunload', () => {
    cleanupRequests();
  });

  /** Performance: cleanup all pending requests when the user switches tabs */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      cleanupRequests();
    }
  });

  /** Performance: cleanup all pending requests when the user closes the tab */
  window.addEventListener('unload', () => {
    cleanupRequests();
  });

  updateSearchResultsCount();
  handleAjax();
  await fetchPreviewText();
};

/** Selector for any instance of this _Friendation_ component */
const selector = '[is="basis-search-results"]';

/** Function that handles a single instance of this _Friendation_ component */
const classFunction: (component: HTMLElement) => void = basisSearchResults;

/**
 * Friendation component
 */
export const aSearchResults = () => {
  /** Instances of this Friendation component */
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach((x) => {
    classFunction.call(this, x);
  });
};
